// One stream per mounted CRM session, discarded on identity or test-set change.
import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { Case, Verdict } from "@artemis-group/aiw-contracts";
import type { CaseUiState } from "@aiw/ui";
import { api, client } from "../lib/api";
import { subscribeCases } from "../lib/sse";
import { getTestSet } from "../lib/testset";
import { supabase } from "../lib/auth";
import { caseState } from "../lib/case-state";

export interface LiveCase { kase: Case; state: CaseUiState; busy: boolean }
const shared = { decidedId: null as string|null, cases: new Map<string, LiveCase>(), listeners: new Set<() => void>(), unsub: null as (() => void) | null, status: "connecting" as "open" | "closed" | "error" | "connecting", scope: null as string | null, epoch: 0, changes: 0, error: null as string | null };
const notify = () => { for (const listener of shared.listeners) listener(); };
function start() {
  if (shared.unsub || !shared.listeners.size) return;
  const epoch = shared.epoch;
  shared.unsub = subscribeCases(k => {
    if (epoch !== shared.epoch) return;
    const previous = shared.cases.get(k.id)?.kase;
    if (previous && (previous.action_status !== k.action_status || previous.delivery_status !== k.delivery_status) && k.action_status !== "pending") shared.changes++;
    shared.cases.set(k.id, { kase: k, state: caseState(k), busy: shared.cases.get(k.id)?.busy ?? false });
    notify();
  }, status => { if (epoch === shared.epoch) { shared.status = status; notify(); } });
}
function reset() {
  shared.epoch++;
  shared.unsub?.(); shared.unsub = null;
  shared.decidedId = null; shared.cases.clear(); shared.error = null; shared.scope = getTestSet(); shared.status = "connecting";
  notify(); start();
}

export function useCases() {
  const qc = useQueryClient();
  const [, tick] = useState(0);
  const mounted = useRef(true);
  const observed = useRef(shared.changes);
  useEffect(() => {
    mounted.current = true;
    const listener = () => { if (mounted.current) { tick(n => n + 1); if (observed.current !== shared.changes) { observed.current = shared.changes; void qc.invalidateQueries(); } } };
    shared.listeners.add(listener);
    if (shared.scope !== getTestSet()) reset(); else start();
    const changed = () => { if (shared.scope !== getTestSet()) reset(); };
    window.addEventListener("aiw:testset", changed);
    window.addEventListener("storage", changed);
    let user: string | null | undefined;
    const auth = supabase()?.auth.onAuthStateChange((_event, session) => {
      const next = session?.user.id ?? null;
      if (user !== undefined && user !== next) reset();
      user = next;
    });
    return () => {
      mounted.current = false; shared.listeners.delete(listener);
      window.removeEventListener("aiw:testset", changed); window.removeEventListener("storage", changed);
      auth?.data.subscription.unsubscribe();
      if (!shared.listeners.size) { shared.unsub?.(); shared.unsub = null; shared.cases.clear(); shared.epoch++; }
    };
  }, [qc]);

  const decide = useCallback(async (id: string, decision: Verdict["decision"], extra?: { reason?: string | null }) => {
    const lc = shared.cases.get(id), epoch = shared.epoch;
    if (!lc || lc.busy || shared.scope !== getTestSet()) return;
    shared.decidedId = id; lc.busy = true; shared.error = null; notify();
    try {
      // A recorded rejection currently returns HTTP 403. Reconcile from the
      // authoritative case even when the verdict request fails or is uncertain.
      let failed = false;
      try { await api.decide(client, { case_id: id, decision, reason: extra?.reason ?? null }); } catch { failed = true; }
      const current = await api.case(client, id);
      if (epoch !== shared.epoch) return;
      shared.cases.set(id, { kase: current, state: caseState(current), busy: false });
      if (failed && current.status === "pending" && current.action_status === "pending") shared.error = "Your decision was not recorded. The proposal is still awaiting approval.";
      await qc.invalidateQueries();
    } catch {
      if (epoch === shared.epoch) { lc.state = "failed"; shared.status = "error"; shared.error = "The decision outcome could not be verified. Refresh before trying again."; }
    } finally {
      if (epoch === shared.epoch) { lc.busy = false; notify(); }
    }
  }, [qc]);
  // Newest first: what just came in is what Jacky is looking for (1 October 2026: "silly design newest should be on top").
  const list = [...shared.cases.values()].sort((a, b) => b.kase.created_at.localeCompare(a.kase.created_at));
  return { decidedId:shared.decidedId,cases: list, open: list.filter(c => c.state === "proposed" || c.state === "revised"), status: shared.status, error: shared.error, decide };
}
